import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DragIndicator, Refresh } from "@mui/icons-material";
import { dashboardAPI, workflowAPI } from "../../services/api";

// Sortable Tile Component
const SortableTile = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: isDragging ? "grabbing" : "grab",
            display: "flex",
            alignItems: "center",
            p: 0.5,
          }}
        >
          <DragIndicator sx={{ color: "text.secondary" }} />
        </Box>
        <Box flex={1} sx={{ pointerEvents: "auto" }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const MIDashView = ({ activeConfigurationId }) => {
  const [workItems, setWorkItems] = useState([]);
  const [workflowStats, setWorkflowStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);
  const [error, setError] = useState("");
  const [hasConnections, setHasConnections] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState("");
  const [estimateView, setEstimateView] = useState("story_points"); // 'story_points', 'hours', 'days'
  const [workflowView, setWorkflowView] = useState("by_state"); // 'by_state', 'by_longest_open'
  const [activeId, setActiveId] = useState(null);
  const [excludeClosedRemoved, setExcludeClosedRemoved] = useState(true); // Default to excluding Closed/Removed/Inactive
  const [layout, setLayout] = useState(() => {
    // Load saved layout from localStorage
    const saved = localStorage.getItem(
      `midash_layout_${activeConfigurationId}`,
    );
    return saved ? JSON.parse(saved) : ["tiles", "chart", "workflow"];
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    if (activeConfigurationId) {
      loadWorkItems();
      loadWorkflowStatistics();
      // Load saved layout for this configuration
      const saved = localStorage.getItem(
        `midash_layout_${activeConfigurationId}`,
      );
      if (saved) {
        setLayout(JSON.parse(saved));
      }
    } else {
      setWorkItems([]);
      setWorkflowStats(null);
    }
  }, [activeConfigurationId]);

  const loadWorkItems = async () => {
    if (!activeConfigurationId) return;

    setLoading(true);
    setError("");
    setHasConnections(false);
    setEmptyMessage("");
    try {
      const response = await dashboardAPI.getWorkItems(activeConfigurationId);

      // Handle new response format (object with workItems) or old format (array for backward compatibility)
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data && response.data.workItems) {
        items = response.data.workItems || [];
        if (response.data.hasConnections && items.length === 0) {
          setHasConnections(true);
          setEmptyMessage(
            response.data.message ||
              "No work items found. Please sync tasks from your DevOps connections in Settings.",
          );
        }
      }

      setWorkItems(items);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load work items.");
      setWorkItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowStatistics = async () => {
    if (!activeConfigurationId) return;

    setLoadingWorkflow(true);
    try {
      const response = await workflowAPI.getStatistics(activeConfigurationId);
      setWorkflowStats(response.data);
    } catch (err) {
      console.error("Failed to load workflow statistics:", err);
      setWorkflowStats(null);
    } finally {
      setLoadingWorkflow(false);
    }
  };

  // Define type bands
  const typeBands = {
    band1: [
      "Epic",
      "Feature",
      "User Story",
      "Work Item",
      "Task",
      "Shared Parameter",
    ],
    band2: ["Bug"],
    band3: ["Test Plan", "Test Suite", "Test Case"],
  };

  // Define type sort order within each band
  const typeSortOrder = {
    Epic: 1,
    Feature: 2,
    "User Story": 3,
    "Work Item": 4,
    Task: 5,
    "Shared Parameter": 6,
    Bug: 1,
    "Test Plan": 1,
    "Test Suite": 2,
    "Test Case": 3,
  };

  // Calculate counts by type and status, grouped by bands
  // Each type will have multiple statuses with counts
  const typeStatusCountsByBand = useMemo(() => {
    const typeCounts = {};
    let filteredItems = workItems;

    // Filter out Closed, Removed, and Inactive if toggle is enabled
    if (excludeClosedRemoved) {
      filteredItems = workItems.filter((item) => {
        const status = (item.status || "").toLowerCase();
        return (
          !status.includes("closed") &&
          !status.includes("removed") &&
          !status.includes("inactive")
        );
      });
    }

    // Group by type, then by status
    filteredItems.forEach((item) => {
      const type = item.work_item_type || "Unknown";
      const status = item.status || "Unknown";

      if (!typeCounts[type]) {
        typeCounts[type] = {
          type,
          statuses: {},
          totalCount: 0,
        };
      }

      if (!typeCounts[type].statuses[status]) {
        typeCounts[type].statuses[status] = 0;
      }

      typeCounts[type].statuses[status]++;
      typeCounts[type].totalCount++;
    });

    // Convert to array format and group by bands
    const band1 = [];
    const band2 = [];
    const band3 = [];
    const other = [];

    Object.values(typeCounts).forEach((typeData) => {
      const statusArray = Object.entries(typeData.statuses)
        .map(([status, count]) => ({
          status,
          count,
        }))
        .sort((a, b) => b.count - a.count); // Sort statuses by count descending

      const typeItem = {
        type: typeData.type,
        statuses: statusArray,
        totalCount: typeData.totalCount,
      };

      if (typeBands.band1.includes(typeData.type)) {
        band1.push(typeItem);
      } else if (typeBands.band2.includes(typeData.type)) {
        band2.push(typeItem);
      } else if (typeBands.band3.includes(typeData.type)) {
        band3.push(typeItem);
      } else {
        other.push(typeItem);
      }
    });

    // Sort each band by type order
    const sortBand = (band) => {
      return band.sort((a, b) => {
        const aOrder = typeSortOrder[a.type] || 999;
        const bOrder = typeSortOrder[b.type] || 999;
        return aOrder - bOrder;
      });
    };

    return {
      band1: sortBand(band1),
      band2: sortBand(band2),
      band3: sortBand(band3),
      other: sortBand(other),
    };
  }, [workItems, excludeClosedRemoved]);

  // Calculate estimates by iteration and status
  const iterationStatusEstimates = useMemo(() => {
    const data = {};
    let filteredItems = workItems;

    // Filter out Closed, Removed, and Inactive if toggle is enabled
    if (excludeClosedRemoved) {
      filteredItems = workItems.filter((item) => {
        const status = (item.status || "").toLowerCase();
        return (
          !status.includes("closed") &&
          !status.includes("removed") &&
          !status.includes("inactive")
        );
      });
    }

    filteredItems.forEach((item) => {
      if (!item.iteration_path) return;

      // Handle both backslash and forward slash separators
      const iteration =
        item.iteration_path.split(/[\\/]/).pop() || item.iteration_path;
      const status = item.status || "Unknown";
      const key = `${iteration}::${status}`;

      if (!data[key]) {
        data[key] = {
          iteration,
          status,
          story_points: 0,
          hours: 0,
          days: 0,
        };
      }

      // Parse and add estimates (handle both string and number types)
      const storyPoints = parseFloat(item.estimate_story_points) || 0;
      const hours = parseFloat(item.estimate_hours) || 0;
      const days = parseFloat(item.estimate_days) || 0;

      data[key].story_points += storyPoints;
      data[key].hours += hours;
      data[key].days += days;
    });

    // Group by iteration for chart (aggregate all statuses)
    const byIteration = {};
    Object.values(data).forEach((item) => {
      if (!byIteration[item.iteration]) {
        byIteration[item.iteration] = {
          iteration: item.iteration,
          story_points: 0,
          hours: 0,
          days: 0,
        };
      }
      byIteration[item.iteration].story_points += item.story_points;
      byIteration[item.iteration].hours += item.hours;
      byIteration[item.iteration].days += item.days;
    });

    // Filter out iterations with no data for the selected view
    const filteredByIteration = Object.values(byIteration).filter((item) => {
      if (estimateView === "story_points") {
        return item.story_points > 0;
      } else if (estimateView === "hours") {
        return item.hours > 0;
      } else {
        return item.days > 0;
      }
    });

    return filteredByIteration.sort((a, b) => {
      // Sort by iteration name (assuming format like "Sprint 1", "Sprint 2", etc.)
      return a.iteration.localeCompare(b.iteration);
    });
  }, [workItems, excludeClosedRemoved, estimateView]);

  const handleEstimateViewChange = (event, newValue) => {
    console.log("Estimate view toggle:", newValue, "Current:", estimateView);
    if (newValue !== null && newValue !== undefined) {
      setEstimateView(newValue);
    }
  };

  const handleWorkflowViewChange = (event, newValue) => {
    console.log("Workflow view toggle:", newValue, "Current:", workflowView);
    if (newValue !== null && newValue !== undefined) {
      setWorkflowView(newValue);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLayout((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newItems = [...items];
        newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, active.id);

        // Save to localStorage
        if (activeConfigurationId) {
          localStorage.setItem(
            `midash_layout_${activeConfigurationId}`,
            JSON.stringify(newItems),
          );
        }

        return newItems;
      });
    }

    setActiveId(null);
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (
      statusLower.includes("done") ||
      statusLower.includes("closed") ||
      statusLower.includes("resolved")
    ) {
      return "success";
    } else if (
      statusLower.includes("in progress") ||
      statusLower.includes("active")
    ) {
      return "info";
    } else if (statusLower.includes("new") || statusLower.includes("to do")) {
      return "default";
    } else if (
      statusLower.includes("blocked") ||
      statusLower.includes("block")
    ) {
      return "error";
    }
    return "default";
  };

  const getTypeColor = (type) => {
    const typeLower = type?.toLowerCase() || "";
    if (typeLower.includes("user story") || typeLower.includes("story")) {
      return "primary";
    } else if (typeLower.includes("bug") || typeLower.includes("defect")) {
      return "error";
    } else if (typeLower.includes("task")) {
      return "default";
    } else if (typeLower.includes("technical debt")) {
      return "warning";
    }
    return "default";
  };

  if (!activeConfigurationId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a configuration to view the MI Dashboard.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" component="h2">
          Management Information Dashboard
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={excludeClosedRemoved}
                onChange={(e) => setExcludeClosedRemoved(e.target.checked)}
                size="small"
              />
            }
            label="Exclude Closed/Removed/Inactive"
          />
          <Tooltip title="Refresh data">
            <IconButton onClick={loadWorkItems} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : workItems.length === 0 ? (
        <Alert severity={hasConnections ? "warning" : "info"}>
          {emptyMessage ||
            "No work items found. Please ensure DevOps connections are configured and verified in Settings. If connections exist, sync tasks using the sync button in Settings > DevOps Settings."}
        </Alert>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={layout}
            strategy={verticalListSortingStrategy}
          >
            <Box>
              {layout.map((itemId) => {
                if (itemId === "tiles") {
                  return (
                    <Box key="tiles" sx={{ mb: 3 }}>
                      <SortableTile id="tiles">
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Work Items by Type & Status
                            </Typography>

                            {/* Band 1: Epic, Feature, User Story, Work Item, Task, Shared Parameter */}
                            {typeStatusCountsByBand.band1.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  gutterBottom
                                  sx={{ mb: 1 }}
                                >
                                  Epic, Feature, User Story, Work Item, Task,
                                  Shared Parameter
                                </Typography>
                                <Grid container spacing={2}>
                                  {typeStatusCountsByBand.band1.map(
                                    (item, index) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                        key={`band1-${index}`}
                                      >
                                        <Card
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            gutterBottom
                                          >
                                            {item.type}
                                          </Typography>
                                          <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                            color="primary"
                                            sx={{ mb: 1 }}
                                          >
                                            {item.totalCount}
                                          </Typography>
                                          <Box sx={{ mt: "auto" }}>
                                            {item.statuses.map(
                                              (statusItem, statusIndex) => (
                                                <Box
                                                  key={statusIndex}
                                                  display="flex"
                                                  justifyContent="space-between"
                                                  alignItems="center"
                                                  sx={{ mb: 0.5 }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    {statusItem.status}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                  >
                                                    {statusItem.count}
                                                  </Typography>
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        </Card>
                                      </Grid>
                                    ),
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {/* Band 2: Bug */}
                            {typeStatusCountsByBand.band2.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  gutterBottom
                                  sx={{ mb: 1 }}
                                >
                                  Bug
                                </Typography>
                                <Grid container spacing={2}>
                                  {typeStatusCountsByBand.band2.map(
                                    (item, index) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                        key={`band2-${index}`}
                                      >
                                        <Card
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            gutterBottom
                                          >
                                            {item.type}
                                          </Typography>
                                          <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                            color="primary"
                                            sx={{ mb: 1 }}
                                          >
                                            {item.totalCount}
                                          </Typography>
                                          <Box sx={{ mt: "auto" }}>
                                            {item.statuses.map(
                                              (statusItem, statusIndex) => (
                                                <Box
                                                  key={statusIndex}
                                                  display="flex"
                                                  justifyContent="space-between"
                                                  alignItems="center"
                                                  sx={{ mb: 0.5 }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    {statusItem.status}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                  >
                                                    {statusItem.count}
                                                  </Typography>
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        </Card>
                                      </Grid>
                                    ),
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {/* Band 3: Test Plan, Test Suite, Test Case */}
                            {typeStatusCountsByBand.band3.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  gutterBottom
                                  sx={{ mb: 1 }}
                                >
                                  Test Plan, Test Suite, Test Case
                                </Typography>
                                <Grid container spacing={2}>
                                  {typeStatusCountsByBand.band3.map(
                                    (item, index) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                        key={`band3-${index}`}
                                      >
                                        <Card
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            gutterBottom
                                          >
                                            {item.type}
                                          </Typography>
                                          <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                            color="primary"
                                            sx={{ mb: 1 }}
                                          >
                                            {item.totalCount}
                                          </Typography>
                                          <Box sx={{ mt: "auto" }}>
                                            {item.statuses.map(
                                              (statusItem, statusIndex) => (
                                                <Box
                                                  key={statusIndex}
                                                  display="flex"
                                                  justifyContent="space-between"
                                                  alignItems="center"
                                                  sx={{ mb: 0.5 }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    {statusItem.status}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                  >
                                                    {statusItem.count}
                                                  </Typography>
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        </Card>
                                      </Grid>
                                    ),
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {/* Other types not in any band */}
                            {typeStatusCountsByBand.other.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  gutterBottom
                                  sx={{ mb: 1 }}
                                >
                                  Other
                                </Typography>
                                <Grid container spacing={2}>
                                  {typeStatusCountsByBand.other.map(
                                    (item, index) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                        key={`other-${index}`}
                                      >
                                        <Card
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            gutterBottom
                                          >
                                            {item.type}
                                          </Typography>
                                          <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                            color="primary"
                                            sx={{ mb: 1 }}
                                          >
                                            {item.totalCount}
                                          </Typography>
                                          <Box sx={{ mt: "auto" }}>
                                            {item.statuses.map(
                                              (statusItem, statusIndex) => (
                                                <Box
                                                  key={statusIndex}
                                                  display="flex"
                                                  justifyContent="space-between"
                                                  alignItems="center"
                                                  sx={{ mb: 0.5 }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    {statusItem.status}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                  >
                                                    {statusItem.count}
                                                  </Typography>
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        </Card>
                                      </Grid>
                                    ),
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {/* Empty state */}
                            {typeStatusCountsByBand.band1.length === 0 &&
                              typeStatusCountsByBand.band2.length === 0 &&
                              typeStatusCountsByBand.band3.length === 0 &&
                              typeStatusCountsByBand.other.length === 0 && (
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      align="center"
                                    >
                                      No work items to display
                                    </Typography>
                                  </Grid>
                                </Grid>
                              )}
                          </CardContent>
                        </Card>
                      </SortableTile>
                    </Box>
                  );
                }

                if (itemId === "chart") {
                  return (
                    <Box key="chart" sx={{ mb: 3 }}>
                      <SortableTile id="chart">
                        <Card>
                          <CardContent>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <Typography variant="h6">
                                Estimates by Iteration
                              </Typography>
                              <ToggleButtonGroup
                                key={`estimate-${activeConfigurationId}`}
                                value={estimateView}
                                exclusive
                                onChange={handleEstimateViewChange}
                                size="small"
                                aria-label="estimate view selection"
                              >
                                <ToggleButton
                                  value="story_points"
                                  aria-label="story points"
                                >
                                  Story Points
                                </ToggleButton>
                                <ToggleButton value="hours" aria-label="hours">
                                  Hours
                                </ToggleButton>
                                <ToggleButton value="days" aria-label="days">
                                  Days
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>
                            {iterationStatusEstimates.length === 0 ? (
                              <Alert severity="info">
                                No iteration data available. Work items need to
                                have an iteration_path and estimate values
                                (hours, days, or story points).
                              </Alert>
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 400,
                                  minHeight: 400,
                                }}
                              >
                                <ResponsiveContainer width="100%" height={400}>
                                  <BarChart data={iterationStatusEstimates}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                      dataKey="iteration"
                                      angle={-45}
                                      textAnchor="end"
                                      height={100}
                                    />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Bar
                                      dataKey={
                                        estimateView === "story_points"
                                          ? "story_points"
                                          : estimateView === "hours"
                                            ? "hours"
                                            : "days"
                                      }
                                      fill="#8884d8"
                                      name={
                                        estimateView === "story_points"
                                          ? "Story Points"
                                          : estimateView === "hours"
                                            ? "Hours"
                                            : "Days"
                                      }
                                    />
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </SortableTile>
                    </Box>
                  );
                }

                if (itemId === "workflow") {
                  return (
                    <Box key="workflow" sx={{ mb: 3 }}>
                      <SortableTile id="workflow">
                        <Card>
                          <CardContent>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <Typography variant="h6">
                                Change Request Workflow Board Overview
                              </Typography>
                              <ToggleButtonGroup
                                key={`workflow-${activeConfigurationId}`}
                                value={workflowView}
                                exclusive
                                onChange={handleWorkflowViewChange}
                                size="small"
                                aria-label="workflow view selection"
                              >
                                <ToggleButton
                                  value="by_state"
                                  aria-label="by state"
                                >
                                  By State
                                </ToggleButton>
                                <ToggleButton
                                  value="by_longest_open"
                                  aria-label="longest open"
                                >
                                  Longest Open
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>

                            {loadingWorkflow ? (
                              <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                              </Box>
                            ) : !workflowStats ? (
                              <Alert severity="info">
                                No workflow data available.
                              </Alert>
                            ) : workflowView === "by_state" ? (
                              <Box>
                                {workflowStats.by_status &&
                                workflowStats.by_status.length > 0 ? (
                                  <Grid container spacing={2}>
                                    {workflowStats.by_status.map(
                                      (item, index) => (
                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={4}
                                          lg={3}
                                          key={`status-${index}`}
                                        >
                                          <Card
                                            variant="outlined"
                                            sx={{ p: 2, height: "100%" }}
                                          >
                                            <Typography
                                              variant="subtitle1"
                                              fontWeight="bold"
                                              gutterBottom
                                            >
                                              {item.display_name || item.status}
                                            </Typography>
                                            <Typography
                                              variant="h4"
                                              fontWeight="bold"
                                              color="primary"
                                            >
                                              {item.count}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ mt: 1 }}
                                            >
                                              Change Request
                                              {item.count !== 1 ? "s" : ""} in
                                              flight
                                            </Typography>
                                          </Card>
                                        </Grid>
                                      ),
                                    )}
                                  </Grid>
                                ) : (
                                  <Alert severity="info">
                                    No change requests in workflow.
                                  </Alert>
                                )}
                              </Box>
                            ) : (
                              <Box>
                                {workflowStats.by_longest_open &&
                                workflowStats.by_longest_open.length > 0 ? (
                                  <Box
                                    sx={{ maxHeight: 400, overflowY: "auto" }}
                                  >
                                    <Grid container spacing={1}>
                                      {workflowStats.by_longest_open.map(
                                        (item, index) => (
                                          <Grid
                                            item
                                            xs={12}
                                            key={`longest-${index}`}
                                          >
                                            <Card
                                              variant="outlined"
                                              sx={{ p: 1.5 }}
                                            >
                                              <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                              >
                                                <Box flex={1}>
                                                  <Typography
                                                    variant="subtitle2"
                                                    fontWeight="bold"
                                                    noWrap
                                                  >
                                                    {item.title}
                                                  </Typography>
                                                  <Box
                                                    display="flex"
                                                    gap={1}
                                                    mt={0.5}
                                                    flexWrap="wrap"
                                                  >
                                                    <Chip
                                                      label={
                                                        item.display_name ||
                                                        item.status
                                                      }
                                                      size="small"
                                                      color="primary"
                                                      variant="outlined"
                                                    />
                                                    <Chip
                                                      label={
                                                        item.card_status ||
                                                        "Active"
                                                      }
                                                      size="small"
                                                      color={
                                                        item.card_status ===
                                                        "Rejected"
                                                          ? "error"
                                                          : item.card_status ===
                                                              "On Hold"
                                                            ? "warning"
                                                            : item.card_status ===
                                                                "Approved"
                                                              ? "info"
                                                              : "success"
                                                      }
                                                      variant="outlined"
                                                    />
                                                  </Box>
                                                </Box>
                                                <Box textAlign="right" ml={2}>
                                                  <Typography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    color="text.secondary"
                                                  >
                                                    {item.time_in_state}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    in state
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Card>
                                          </Grid>
                                        ),
                                      )}
                                    </Grid>
                                  </Box>
                                ) : (
                                  <Alert severity="info">
                                    No change requests found.
                                  </Alert>
                                )}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </SortableTile>
                    </Box>
                  );
                }

                return null;
              })}
            </Box>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Card sx={{ opacity: 0.8, maxWidth: 300 }}>
                <CardContent>
                  <Typography variant="subtitle1">
                    {activeId === "tiles"
                      ? "Work Items by Type & Status"
                      : activeId === "chart"
                        ? "Estimates by Iteration"
                        : activeId === "workflow"
                          ? "Change Request Workflow Board Overview"
                          : ""}
                  </Typography>
                </CardContent>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </Paper>
  );
};

export default MIDashView;
